/**
 * User profile and tournament roster types and Firestore helpers.
 *
 * - users/{uid} — User profile (display name via updateUserName callable)
 * - users/{uid}.supportPromptNever — client may set true once (Support CTA on /profile; see setSupportPromptNever)
 * - users/{uid}.hasSupported — client may set true once (after Support thanks; see setHasSupported)
 * - users/{uid}/nameViolations/{id} — Offensive-name audit log (server-written)
 * - users/{uid}/surveyResponses/player-v1 — Completed player survey (create-once; see playerSurvey.ts)
 * - users/{uid}/tournaments/{tid} — TeamSelectionRecord (10-team list, scores, mdReset)
 */
import {
  collection,
  collectionGroup,
  doc,
  type Firestore,
  getDoc,
  query,
  type Query,
  type QueryConstraint,
  serverTimestamp,
  setDoc,
  type Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { ACTIVE_TOURNAMENT } from '../constants';
import type { Country } from './country';
import { sentinelType } from './gameUpdate';

export default interface User {
  /**
   * Denormalized mirror of the Firebase Auth `admin` custom claim (kept in sync by
   * `toggleAdminRights`). Client route gates use `claims.admin`, not this field.
   */
  admin: boolean;
  name: string;
  email: string;
  /** Short share URL when present; web share flow primarily uses query-string `/list` links. */
  shareShortLink: string | null;
  /** Set after list edits until share assets are refreshed; admin bulk-clear via updateRequireShareUpdate. */
  requiresShareUpdate: boolean;
  /** Marketing opt-out; also writable unsigned via `/unsubscribe/:userID`. */
  excludeMarketing?: boolean;
  createdAt?: Timestamp;
  /** Shown once after a name policy violation reset */
  namePolicyWarningPending?: boolean;
  /** Offensive display-name strikes; incremented only by admin Enforce selected */
  offensiveNameStrikeCount?: number;
  /** Set by server when moderation resets a name */
  nameModeratedAt?: Timestamp;
  /** True after the player survey is submitted; client may set true once only */
  playerSurveyCompleted?: boolean;
  /** True after “Never” on the Support CTA; client may set true once only */
  supportPromptNever?: boolean;
  /** True after returning from a Support payment (thanks page); client may set true once only */
  hasSupported?: boolean;
}

export type NameViolationSource =
  | 'signup'
  | 'profile_update'
  | 'callable'
  | 'batch_scan'
  | 'scheduled_scan'
  | 'admin_delete';

export type NameViolationAction =
  | 'warn_and_reset'
  | 'account_deleted'
  | 'rejected_before_write'
  | 'silent_reset';

export interface NameViolationRecord {
  originalName: string;
  normalizedName: string;
  matchCategory: string;
  source: NameViolationSource;
  action: NameViolationAction;
  detectedAt: Timestamp;
}

export interface GlobalNameViolationRecord extends NameViolationRecord {
  uid: string;
  email: string;
}
export interface TeamSelectionRecord {
  tournament: 'mwc-22' | 'wwc-23' | 'euro-24' | 'weuro-25' | 'wc-26';
  countryList: Country[];
  prevScore: number;
  currentScore: number;
  /**
   * @deprecated Legacy score-engine field. Not written or read by the MatchDay engine;
   * still listed in `firestore.rules` `scoreFieldsUntouched` so clients cannot set it.
   */
  potentialScore?: number;
  /**
   * @deprecated Retired with the MatchDay scoring transition. League tie-break is now
   * `currentScore` desc, then `tieBreakAt`, then `uid` (see `tieBreakAt`). Left on existing docs
   * for backwards compatibility.
   */
  goalsGuess: number;
  scoreLastUpdated: Timestamp;
  listIndexSwapPair: [number, number] | null;
  latecomer: boolean | undefined;
  spectator: boolean | undefined;
  /** Pre-tournament list-edit timestamp; may feed legacy tie-break / migration paths. */
  listUpdatedAt?: Timestamp;
  swapUpdatedAt?: Timestamp;

  // --- MatchDay scoring fields (see docs/matchday-scoring/) ---
  /**
   * Baseline MatchDay. Points accrued through MatchDay `mdReset` are forfeited; scoring resumes
   * from `mdReset + 1`. 0 = scores from the start of the tournament. Set to the current MatchDay on
   * RESET and on late first submission.
   */
  mdReset?: number;
  /** Roster submission timestamp for league tie-break; updated on pre-tournament edits, INITIAL, and RESET (never on SWAP). */
  tieBreakAt?: Timestamp;
  /** True when the first full submission happened after the first kickoff. Set once, never recomputed. */
  isLate?: boolean;
  /** Per-MatchDay cumulative user score U; length MATCHDAY_COUNT+1, U[0]=0. */
  scoreHistory?: number[];
  /** When true, league members may view this user's 10-team list in-app. */
  listVisibleInLeagues?: boolean;
}

export interface TeamSelectionData {
  [key: string]: TeamSelectionRecord;
}

export interface leagueMember {
  name: string;
  currentRank: number;
  prevRank: number;
  currentScore: number;
  prevScore: number;
  /**
   * @deprecated Pre–MatchDay tie-break field. Not written by current settlement;
   * ranking uses `currentScore` / `tieBreakAt` / `uid` (see `leagueRanking.ts`).
   */
  guessError: number | undefined;
  /** Denormalized for collection-group queries of a user's memberships. */
  userID: string;
  joinedLeague: Timestamp;
  /** True when users/{uid}/tournaments/wc-26 has a complete 10-team list. */
  hasList: boolean | undefined;
  latecomer: boolean | undefined;
  spectator: boolean | undefined;
  /** 1-based order when the user joined the league (join sequence). */
  listPosition?: number;
  /** Zero-based leaderboard page index (page size 50); set when ranks update. */
  leaderboardPage?: number;
  rankLastUpdated: Timestamp | undefined;
  /** Denormalized from the user's tournament doc; drives underlined names on league leaderboards. */
  listVisibleInLeagues?: boolean;
}

export async function updateUser(db: Firestore, userID: string, data: Partial<User>): Promise<void> {
  return await updateDoc(doc(db, 'users', userID), data);
}

export async function excludeUserFromMarketing(db: Firestore, userID: string): Promise<void> {
  return await updateDoc(doc(db, 'users', userID), { excludeMarketing: true });
}

/** Permanently suppress the /profile Support CTA (rules: set true once). */
export async function setSupportPromptNever(db: Firestore, userID: string): Promise<void> {
  return await updateDoc(doc(db, 'users', userID), { supportPromptNever: true });
}

/** Mark that the signed-in user completed a Support payment (rules: set true once). */
export async function setHasSupported(db: Firestore, userID: string): Promise<void> {
  return await updateDoc(doc(db, 'users', userID), { hasSupported: true });
}

export async function updateTeamSelectionData(
  db: Firestore,
  userID: string,
  data: Partial<TeamSelectionRecord>,
): Promise<void> {
  const teamDoc = doc(db, 'users', userID, 'tournaments', ACTIVE_TOURNAMENT);
  const docSnap = await getDoc(teamDoc);
  if (docSnap.exists()) {
    return await updateDoc(teamDoc, data);
  } else {
    const gameStartedDoc = await getDoc(
      doc(db, 'competitions', ACTIVE_TOURNAMENT, 'gameUpdates', sentinelType.tournamentStarted),
    );
    const tournamentStarted = gameStartedDoc.exists();

    const teamSelectionRecord = {
      tournament: ACTIVE_TOURNAMENT,
      countryList: [],
      currentScore: 0,
      prevScore: 0,
      goalsGuess: 0,
      scoreLastUpdated: serverTimestamp(),
      listIndexSwapPair: null,
      latecomer: tournamentStarted,
      spectator: true,
    };
    // Create the plain doc first + then update it. This forces an onTeamSelectionUpdated call as required
    await setDoc(teamDoc, { ...teamSelectionRecord });
    return await updateDoc(teamDoc, data);
  }
}

export function queryUsers(db: Firestore, ...queries: QueryConstraint[]): Query {
  return query(collection(db, 'users'), ...queries);
}

export function queryUserTeams(db: Firestore, ...queries: QueryConstraint[]): Query {
  return query(collectionGroup(db, 'tournaments'), ...queries);
}
